import { AppDate } from "../envTypes";
import { CommunityChannelLimitSettings } from "./community-channel";
import { DeepPartial, EditorialTextFormat } from "./shared";
import { PopStoreOnboardingContestConfig } from "./onboarding-contest";
import { SellerOnboardingConfig } from "./seller-onboarding";
import {
  SaasSubscriptionBasePlanType,
  SubscriptionPlanInterval,
} from "./saas-subscription";
import { PopStoreCreatorBusinessType } from "./profile";
import { TokenCostType } from "./token";

export type VideoCoverRenderAsType = "video" | "gif" | "image" | "auto";
export type VideoCoverImageType = "thumbnail" | "product-image";
export type NativeCompressVideoType =
  | "popclip"
  | "order-video"
  | "upcoming-preview"
  | "store-reel";

export type VideoCompressionConfig = {
  method?: "auto" | "manual";
  maxSize?: number;
  resolution?: number;
};

/**
 * A card the QA team signs up with to exercise the real SaaS checkout. See
 * {@link SaasTestCardAutoRefundConfig}.
 *
 * The card is identified the way Stripe reports it on the PaymentMethod,
 * `brand` / `last4` / `exp_month` / `exp_year`, because the card number itself
 * never reaches the backend. Those four must agree on every match;
 * `fingerprint`, Stripe's exact identity for the card number, is filled in by
 * the backend the first time they do and must agree too from then on — so a
 * different card sharing the four details cannot slip in, and a renewed card
 * (new expiry) needs its entry updated.
 */
export interface TestCard {
  /** Free text for the admin page, e.g. "Company Amex". */
  label?: string;
  /** Lower-case Stripe brand: `visa`, `mastercard`, `amex`, ... */
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  /** Stripe's `card.fingerprint`; back-filled on first match, required after. */
  fingerprint?: string | null;
  /**
   * A signup paid with this card skips every trial so the plan is charged in
   * full on day one, and a job refunds every invoice of that subscription and
   * cancels it `auto_refund_after_seconds` later.
   *
   * ABSENT MEANS ON: this is what an entry in `test_card_auto_refund` meant
   * before the flag existed, so entries written without it keep refunding.
   * Only an explicit `false` turns it off.
   */
  auto_refund?: boolean;
  /**
   * This card skips the SaaS card verification (the prepaid check, the $1
   * authorisation hold and the Radar risk score) when it is added and when a
   * subscription is created with it. For corporate virtual cards, which Stripe
   * reports as prepaid and Radar scores high.
   *
   * ABSENT MEANS OFF: only an explicit `true` opens this door.
   *
   * Both flags off leaves the entry inert — it still records the card's
   * fingerprint on a match, nothing else.
   */
  skip_verification?: boolean;
}

/**
 * Internal QA hook on the SaaS checkout, keyed per card by the two flags on
 * {@link TestCard}: `auto_refund` — a signup paid with the card skips every
 * trial so the plan is charged in full on day one, and a job refunds every
 * invoice of that one subscription and cancels it `auto_refund_after_seconds`
 * later; `skip_verification` — the card is accepted whatever the SaaS card
 * verification would say about it. The key name predates the second flag.
 *
 * ABSENT OR `enabled: false` MEANS OFF for both, and off means the signup
 * path is untouched — the card is not even looked at. The cards are real
 * cards (a Stripe test number is declined in live mode), so the refund is
 * what keeps a test signup from costing the plan price; Stripe's processing
 * fee is not returned either way.
 */
export interface SaasTestCardAutoRefundConfig {
  /** Master switch over every card and both flags. */
  enabled?: boolean;
  /** Delay before the refund job runs, in seconds. Default 3 days (259200). */
  auto_refund_after_seconds?: number;
  cards?: TestCard[];
}

/**
 * The win-back offer for a creator who cancelled: a single-use percent-off
 * coupon minted for them, on request, for the email that invites them back.
 * The email and its timing belong to the Popstore AI service, whose cron
 * lists the cancelled plans about to end and asks for each creator's coupon.
 * Off unless `enabled` is true.
 *
 * The discount itself — percent off and how many invoices it repeats over —
 * is NOT configured here: it is the same offer the cancel flow makes, read
 * from {@link SaasRetentionOfferConfig} (`retention_offer.discount_percent`
 * and `retention_offer.duration_months`), so the admin edits one place. The
 * keys here are set directly on the Firestore document; the admin has no
 * form for them.
 */
export interface SaasWinBackOfferConfig {
  /** Master switch. Default off. */
  enabled?: boolean;
  /** Days after minting the code stays redeemable. Default 30. */
  coupon_valid_days?: number;
}

/** One plan's standing discount on the creator plan picker. */
export interface SaasPlanCoupon {
  /**
   * The plan's Stripe price id, or {@link kAllPlansCouponId} for the fallback
   * that covers any plan with no entry of its own — so a newly launched tier is
   * discounted without a config edit. A specific plan id always wins over it.
   */
  planId: string;
  /**
   * Which billing interval the fallback covers. REQUIRED when `planId` is
   * {@link kAllPlansCouponId}, ignored otherwise — a specific price id already
   * IS one interval.
   *
   * Without it a fallback is interval-blind, so a monthly-only coupon assigned
   * to "all plans" would also discount the annual plans, quoting a price
   * checkout would refuse. There is one fallback per interval.
   */
  interval?: SubscriptionPlanInterval;
  /**
   * The coupon's CODE — its `saasSubscriptionCoupons` document id, which is what
   * the admin's Coupons page prints in its COUPON CODE column and what checkout
   * applies. NOT the coupon's `name`, which is the human label beside it.
   */
  couponCode: string;
}

/** `planId` marking the entry that applies to every otherwise-unassigned plan. */
export const kAllPlansCouponId = "*";

/**
 * Which plans the PAID trial is offered on — Stripe price ids, or
 * {@link kAllPlansPaidTrialId} for "every plan".
 *
 * A LIST, not a per-plan record, because there is nothing per-plan to store:
 * the FEE lives on the plan itself (`SubscriptionPlan.paidTrialAmount`, created
 * in Stripe alongside the `paidTrialCouponId` that makes it chargeable), so all
 * this has to answer is WHICH plans may offer it. Selection is a config edit;
 * pricing stays where the money is.
 *
 * MONTHLY by nature, so there is no interval dimension here and `*` does not
 * need one — unlike {@link SaasPlanCoupon}'s fallback, which has to name an
 * interval because a monthly coupon must not discount an annual plan. Every
 * offer is monthly-only (an annual card prices at list before any offer is
 * considered), so `*` means every plan that could carry one.
 *
 * ABSENT vs EMPTY are different answers and both are meaningful:
 *   - ABSENT  -> not configured; every plan carrying an amount may offer it,
 *                which is the behaviour that predates this key.
 *   - []      -> configured as NONE; no plan offers a paid trial, even with
 *                `paid_trial_enabled` on.
 *
 * It NARROWS, never widens: a plan listed here still offers nothing unless it
 * carries a `paidTrialAmount`, exactly as a plan named in `plan_coupons` still
 * prices at list if its code is stale.
 */
export type SaasPaidTrialPlans = string[];

/** The `paid_trial_plans` entry meaning every plan. */
export const kAllPlansPaidTrialId = "*";

/** How long the paid trial runs, in days. */
export type SaasPaidTrialDays = 7 | 15 | 21;

/**
 * The lengths the admin offers, in order.
 *
 * A closed set rather than a free number: the span is quoted on the plan card
 * ("On day 7 · $75"), scheduled in Stripe and repeated in the creator's
 * receipt, so an arbitrary value is three places to be wrong at once. Extend
 * this list to add one — nothing else enumerates them.
 */
export const kSaasPaidTrialDayOptions: SaasPaidTrialDays[] = [7, 15, 21];

export interface PublicConfigTextContent {
  format: EditorialTextFormat;
  value: string;
  title?: string;
  hidden?: boolean;
  collapsible?: boolean;
}

export type CommentChatUrlsConfig = {
  facebookBusinessPageHelpUrl: string;
  instagramProfessionalAccountHelpUrl: string;
  instagramEnableDMsHelpUrl: string;
  facebookBusinessManagerUrl: string;
  instagramConnectFacebookBusinessPageHelpUrl?: string;
  missingAccountsHelpUrl: string;
};

export type TokenCosts = {
  [p in TokenCostType]: number;
};

/**
 * What one Growth RX diagnostic run costs. Named rather than inlined below
 * because the PREWARM price is defined as a fraction of it and must move with
 * it — see `GROWTH_RX_PREWARM_CHARGE_RATIO`.
 */
export const GROWTH_RX_DIAGNOSTIC_TOKEN_COST = 10;

/**
 * What a prewarmed run costs, as a fraction of a diagnostic.
 *
 * A prewarm is the SAME work — diagnose the creator's posts, then write the
 * plan — started the moment they pay rather than when they open the agent, so
 * the page is a read instead of a ~68 second wait. They pay half for it, because
 * nobody asked for the run.
 *
 * ⚠️ Mirrored as `GROWTH_RX_PREWARM_CHARGE_RATIO` in popstore-ai-service
 * (`src/agents/sea/_seaHelpers.ts`), which cannot import this file. That copy
 * documents the intent at the charge site; THIS one is what is actually
 * charged, and it must also be applied the same way in `DEFAULT_TOKEN_COSTS_V2`
 * (`ServerModels.ts`), which is what prices the order.
 */
export const GROWTH_RX_PREWARM_CHARGE_RATIO = 0.5;

export const DEFAULT_TOKEN_COSTS: TokenCosts = {
  [TokenCostType.AIPhoto]: 10,
  [TokenCostType.AIVideo5s]: 20,
  [TokenCostType.AIVideo10s]: 30,
  [TokenCostType.AIVideo15s]: 50,
  [TokenCostType.AIVideoPerSecond]: 5,
  [TokenCostType.AIVideo480pPerSecond]: 2,
  [TokenCostType.AIVideo720pPerSecond]: 5,
  [TokenCostType.AIVideo1080pPerSecond]: 10,
  [TokenCostType.AIStoryboardPreview]: 15,
  [TokenCostType.AIVideoCustomMotion]: 30,
  [TokenCostType.LipSyncAddon]: 5,
  [TokenCostType.PhotoShoot]: 50,
  [TokenCostType.FullyAutoPDF]: 100,
  [TokenCostType.AIAssistedPDF]: 200,
  [TokenCostType.EditPDF]: 5,
  [TokenCostType.AIDMResponseCommentchat]: 0.05,
  [TokenCostType.AICommentResponseCommentchat]: 0.05,
  [TokenCostType.EchoCommentGeneration]: 0.09,
  [TokenCostType.DMOfferAnalysis]: 0.1,
  [TokenCostType.AICommentAnalysis]: 0.05,
  [TokenCostType.AIPostAnalysis]: 0.14,
  [TokenCostType.AICommentIntentOfferMatching]: 0.14,
  [TokenCostType.AITemplateCustomization]: 10,
  [TokenCostType.FollowerProfileScrape]: 0.05,
  [TokenCostType.CollabPostCommentScrape]: 0.75,
  /**
   * Growth RX. These MUST equal `DEFAULT_TOKEN_COSTS_V2` in `ServerModels.ts`,
   * which is what actually prices the order. This table is the fallback for the
   * PUBLIC price lookup (`POST /v1/token-gated-content:get-price`), so a
   * different number here is a price SHOWN that is not the price CHARGED.
   *
   * One diagnostic run and one script are each ONE unit — popstore-ai-service
   * charges quantity 1 per run and 1 per script — so these are the totals a
   * creator sees and pays, with no multiplier anywhere in between.
   */
  [TokenCostType.GrowthRxDiagnostic]: GROWTH_RX_DIAGNOSTIC_TOKEN_COST,
  [TokenCostType.GrowthRxScript]: 2,
  /**
   * DERIVED, not a literal, and deliberately so: a prewarm is defined as half a
   * diagnostic, and writing 5 here would let the two drift the next time the
   * diagnostic is repriced. `Math.round` because a token count is a whole number
   * and an odd diagnostic price must still resolve to one.
   */
  [TokenCostType.GrowthRxPrewarm]: Math.round(
    GROWTH_RX_DIAGNOSTIC_TOKEN_COST * GROWTH_RX_PREWARM_CHARGE_RATIO,
  ),
  /** One post score; popstore-ai-service charges quantity 1 per scored post. */
  [TokenCostType.GrowthRxPostScore]: 2,
  [TokenCostType.VideoChapter]: 1,
};

/** See `PublicConfig.saas_subscription.retention_offer`. */
export interface SaasRetentionOfferConfig {
  /** Percent off the discount offer takes, e.g. 30. */
  discount_percent?: number;
  /** How many invoices the discount covers (a `repeating` coupon). Unset: the next invoice only. */
  duration_months?: number;
  /** How long either offer stays acceptable once shown. */
  offer_duration_minutes?: number;
  /** AI credits the free-month offer grants on top of the free invoice. */
  bonus_credits?: number;
  /** Followers at or above which "too complicated" offers a setup call rather than the video. */
  setup_call_followers_threshold?: number;
}

export interface PublicConfig {
  enable_cs_iframe_proxy?: boolean;
  auction_timer_countdown_seconds: number;
  upvote_pin_time: {
    initial: number;
    additional: number;
  };
  // toggle the display of top reviews row on the discovery page
  discovery_top_reviews?: boolean;
  enable_referral_gifting?: boolean;
  force_merge_account_for_signup_after?: AppDate;
  psl_pay_remind_after?: AppDate;
  psl_pay_enforced_after?: AppDate;
  variant_price_enable?: boolean;
  enable_seller_onboarding?: boolean;
  allow_remove_categories?: boolean;

  stripe_required?: boolean;
  braintree_required?: boolean;

  apple_pay_use_stripe?: boolean;
  apple_pay_use_psl_as_merchant_name?: boolean;

  auto_featured_show_cap?: {
    max_auto_featured_live_stream_quantity: number;
    max_auto_featured_upcoming_show_quantity: number;
  };
  seller_onboarding?: SellerOnboardingConfig;
  psl_features?: {
    enable_pop_clips_seller: boolean;
    enable_pop_clips_buyer: boolean;
  };
  video_cover?: {
    with_image_cover_bg?: boolean;
    image_cover_type?: VideoCoverImageType;
    ios?: VideoCoverRenderAsType;
    android?: VideoCoverRenderAsType;
    web?: VideoCoverRenderAsType;
  };
  native_compress_videos?: NativeCompressVideoType[];
  order_videos?: {
    compression?: VideoCompressionConfig;
  };
  pop_clips?: {
    compression?: VideoCompressionConfig;
  };
  moderation?: {
    user_blocking?: boolean;
  };
  discovery?: {
    use_featured_show_impl_for_event?: boolean;
  };
  auth?: {
    facebook_login_enabled?: boolean;
    required_for_mobile?: boolean;
    login_remind_enabled?: boolean;
    login_remind_max_count?: number;
    initial_login_remind_delay?: number;
    recurrent_login_remind_interval?: number;
  };
  commentchat?: {
    enabled_for_all_sellers_by_default?: boolean;
    enabled_in_seller_dashboard?: boolean;
    enabled_in_app?: boolean;
    instagram_dm_check_enabled?: boolean;
    urls?: Partial<CommentChatUrlsConfig>;
    connect_ig_channels_only?: boolean;
    max_dm_response_count?: number;
  };
  shopify_sync?: {
    enabled_in_seller_dashboard?: boolean;
    enabled_in_app?: boolean;
  };
  pdp?: {
    show_sku_label?: boolean;
    show_dropship_brand_partner_label?: boolean;
    show_dropship_supplier_name_label?: boolean;
    show_seller_link?: boolean;
    hide_single_variant?: boolean;
    refund_return_text_copy?: PublicConfigTextContent;
    min_inventory_level_for_hide_qty?: number;
    shipping_to_us_only?: PublicConfigTextContent;
    rating_count_visible_threshold?: number;
    apple_pay?: {
      deduplicate_stripe_payment_method?: boolean;
    };
    openbox?: {
      explicit_free_shipping?: PublicConfigTextContent;
      free_shipping?: PublicConfigTextContent;
      free_shipping_available?: {
        psl?: PublicConfigTextContent;
        dropship?: PublicConfigTextContent;
      };
    };
  };
  msm?: {
    enabled?: boolean;
    help_article_url?: string;
  };
  profile_page?: {
    creator?: {
      stats_visible_min_sold_items?: number;
      hours_live_visible_min_hours?: number;
    };
  };
  modelme?: {
    beta?: boolean;
    disabled_for_business_seller?: boolean;
    min_face_photos?: number;
    min_standing_photos?: number;
    max_allowed_image_size_in_mb?: number;
  };
  popstore?: {
    show_on_min_followers?: number;
    video_overlay_auto_hide_in_seconds?: number;
    video_touch_to_hide_overlay_threshold?: number;
    gated_mini_player_blurred_in_seconds?: number;
    gated_mini_player_blurry_intensity?: number;
    mini_player_support_content_types?: (
      | "upcomingShow"
      | "liveShow"
      | "replay"
      | "videoClip"
      | "storeReel"
    )[];
    onboarding_contest?: PopStoreOnboardingContestConfig;
    popstore_avatar_me_enabled?: boolean;
    popstore_event_enabled?: boolean;
    /**
     * Marketing events selectable as `utm_eventname` in the admin campaign URL
     * builder. Config-driven so a new event (real estate, etc.) is a config
     * edit rather than an admin deploy. `label` falls back to `value` when
     * absent; `disabled` hides an option from the picker without dropping it,
     * so existing links keep resolving.
     */
    popstore_event_names?: {
      value: string;
      label?: string;
      disabled?: boolean;
    }[];
    /**
     * Global kill-switch for the shortened real-estate express setup. When
     * true, real-estate creators (`profile.businessType === RealEstate`) only
     * complete the Template step of express setup — the remaining steps
     * (contact card, lead form, lead magnet, live) are skipped and the wizard
     * is finalized straight after Template. Off / absent = full express setup
     * for everyone.
     */
    realestate_express_setup_template_only?: boolean;
    /**
     * How many times a buyer may fetch a purchased digital product. Absent or
     * `null` is unlimited. Read at download time, so a change applies to every
     * purchase already made, not only new ones.
     */
    digital_download_limit_count?: number | null;
    /**
     * Global kill-switch for the FAQ / AlterEgo agent features. When true, the
     * FAQ agents (and related agent functionality) are enabled; when false or
     * absent, the feature is disabled everywhere.
     */
    echo_faq_agent_enabled?: boolean;
    /**
     * Social sign-in on the login form — the member Facebook/Google row and the
     * creator Facebook/Instagram/Apple row on `PhoneAuthForm`.
     *
     * SCOPE IS THE POPSTORE MANAGER APP, NOT THE WEB. On the web these rows are
     * unconditional and this key does not reach them; inside the native shell
     * they are hidden unless it is `true`. That asymmetry is the feature: the
     * app is the surface being held back, and turning this on is what releases
     * it there.
     *
     * ABSENT MEANS OFF, so shipping the flag hides social sign-in in the app
     * until someone deliberately enables it.
     *
     * GLOBAL ONLY — there is no per-seller or per-user variant here, and that is
     * a constraint rather than an omission. The app opens the context-less
     * `/login?reg=creator`, which carries no creator, and the rows render before
     * anyone has authenticated: at that moment there is no identity of any kind
     * to target. Anything narrower has to key on something the device itself
     * knows, which is why the next step is a device-id allowlist rather than a
     * user one.
     */
    social_login?: {
      /** `true` shows the rows inside the manager app. The web is unaffected. */
      enabled?: boolean;
    };
    /**
     * The pre-paywall content taste (`/taste`) on the Growth Rx onboarding
     * path — the screen that shows a signing-up creator what ECHO wrote in
     * their own voice before the pricing picker.
     *
     * ABSENT MEANS OFF. The taste is held back by default, and turning this on
     * is what puts the detour back: the signup finalize step routes a Growth Rx
     * creator to it, the plan gate detours a plan-less one into it, and the
     * route itself renders instead of bouncing to `/pricing-plans`. All three
     * read this one key, so it cannot half-apply.
     *
     * GLOBAL ONLY — the taste is either part of the Growth Rx path or it is
     * not; there is no per-seller variant, and the creator it targets is being
     * created as it is decided.
     */
    taste_enabled?: boolean;
    supported_alternative_domains?: {
      domain: string;
      disabled?: boolean;
      min_saas_subscription_plan_required?: SaasSubscriptionBasePlanType;
    }[];
    independent_domain?: {
      min_saas_subscription_plan_required?: SaasSubscriptionBasePlanType;
    };
    community?: {
      channelLimitSettings?: DeepPartial<CommunityChannelLimitSettings>;
    };
  };
  font_config?: string;
  token_system?: {
    prices: TokenCosts;
    low_balance_threshold?: number;
  };
  saas_subscription?: {
    /**
     * Whether a plan's free trial is offered at all.
     *
     * Trials were retired in favour of the standing discount
     * ({@link SaasPlanCoupon}); this is the switch that brings them back without
     * a deploy. It gates the OFFER, not the data: plans keep their
     * `trialPeriodDays`, so flipping this on restores the trial CTA, the
     * "N days free" badge and the trial billing lines exactly as they were.
     *
     * ABSENT MEANS OFF. The retirement shipped, so a config that has never
     * carried this key must keep behaving as it does today — a default of `true`
     * would silently reinstate trials everywhere on deploy.
     *
     * Distinct from {@link paid_trial_enabled}, which gates the PAID-trial
     * pathway (a reduced first charge) rather than the free one.
     */
    free_trial_enabled?: boolean;
    /**
     * Plans whose `businessType` is listed here never get the free trial, even
     * with {@link free_trial_enabled} on — e.g. `['aiecho']` keeps the trial
     * off the ECHO-ME plans while real estate gets it.
     *
     * An exclusion list rather than an allow list on purpose: the switch means
     * "every plan", so absent or empty keeps that, and a plan type added later
     * follows the switch like every other. A plan with no `businessType` cannot
     * be excluded.
     *
     * Excluding a plan withdraws the trial only. The admin wrote the paid trial
     * and the discount off when it switched the free trial on, so an excluded
     * plan sells at list price.
     */
    free_trial_excluded_business_types?: PopStoreCreatorBusinessType[];
    paid_trial_enabled?: boolean;
    /**
     * Whether the standing per-plan discount ({@link plan_coupons}) is offered.
     *
     * ** ABSENT MEANS ON — the opposite of the two switches above. **
     * Only an explicit `false` switches discounts off.
     *
     * That asymmetry is deliberate and was learned the hard way. The two trials
     * did not exist before their keys did, so absent-means-off is a
     * continuation for them. The standing discount is the opposite: it has been
     * applying since before this key existed, whenever no trial did, with
     * nothing to switch. Reading absent as OFF therefore SILENTLY WITHDRAWS
     * every live discount the moment a reader ships — observed exactly that way,
     * with `/trial-availability` returning a `percent_off` coupon and the plan
     * cards printing full price.
     *
     * The third of THREE MUTUALLY EXCLUSIVE offer switches. Exactly one offer
     * reaches a creator, never a combination:
     *
     *   free_trial_enabled     -> the free trial
     *   paid_trial_enabled     -> the paid trial
     *   discount_enabled       -> the plan's discount
     *   none                   -> list price
     *
     * The admin enforces the exclusivity as it writes — enabling one writes the
     * other two off in the SAME update, so the document cannot hold two.
     *
     * ** THERE ARE NO COMBINATIONS. ** A `paid_trial_discount` key briefly
     * existed to apply this discount on top of the paid trial; it was removed
     * before shipping because the offer is either/or by design — a creator
     * picks the 7-day trial OR the discount, and stacking them made the price a
     * creator would pay impossible to state in one line. Do not reintroduce it.
     * A reader should still RANK them (`free > paid > discount`) rather than
     * trust that: a document written by hand, or by a build predating the
     * exclusivity, can carry two, and picking the first is the only answer that
     * does not depend on field order.
     *
     * Per PLAN either way: a plan with no `plan_coupons` entry (and no `*`
     * fallback for its interval) is at list price however this is set, exactly
     * as a plan with no paid-trial amount offers no paid trial.
     */
    discount_enabled?: boolean;
    /**
     * Which plans {@link paid_trial_enabled} actually applies to — see
     * {@link SaasPaidTrialPlans} for the shape and for what absent, empty and
     * `*` each mean.
     *
     * The switch says the platform offers a paid trial; this says on which
     * plans. The pair mirrors {@link plan_coupons} beside
     * {@link discount_enabled}, so both per-plan offers are configured the same
     * way and on the same screen.
     */
    paid_trial_plans?: SaasPaidTrialPlans;
    /**
     * How long the paid trial runs, platform-wide.
     *
     * ABSENT means the PLAN's own `trialPeriodDays` decides, which is what
     * happened before this key existed — so an unwritten config keeps today's
     * behaviour and a creator's card keeps quoting the span their charge is
     * actually scheduled against.
     *
     * ⚠️ IT OVERRIDES WHAT THE CARD SAYS, NOT WHAT STRIPE DOES. The real date is
     * set when the subscription is created, from the plan's Stripe trial. Until
     * the backend reads this key too, a value that disagrees with the plan makes
     * the card quote a day the charge will not land on — which is worse than
     * having no control at all. Set it to match the plans, or change the plans.
     */
    paid_trial_days?: SaasPaidTrialDays;
    no_credit_card_trial_first_month_credits?: number;
    /**
     * Whether taking the ECHO-ME Free plan needs a card on file. Off, a
     * seller gets a $0 Stripe subscription with no payment method and adds a
     * card with their first credit add-on; on, the signup collects one like
     * any paid plan. Read live on every free signup.
     */
    free_plan_requires_card?: boolean;
    /**
     * The cancellation flow's save offers. Read live on every request, so an
     * admin edit applies at once. Absent keys fall back to the code defaults,
     * which are the ORIGINAL save offer (half off one invoice): a config that
     * has never carried this map keeps behaving as production does today.
     */
    retention_offer?: SaasRetentionOfferConfig;
    /**
     * The standing discount offered on the plan picker, one entry per plan.
     *
     * Trials are retired; this is what replaced them. Marketing creates the
     * coupon on the admin's Coupons page and assigns it to plans on the SaaS
     * Plan Coupons page, so changing the offer needs no deploy.
     *
     * At most ONE entry per `planId` — assigning a second replaces the first.
     * Monthly and annual are different price ids, so an offer can cover one
     * interval and not the other.
     *
     * The code is resolved against the coupon API before anything is shown or
     * charged, so a stale or deleted code simply prices at list.
     */
    plan_coupons?: SaasPlanCoupon[];
    /**
     * QA-only: sign up with a configured card and, per card, get charged for
     * real then refunded and cancelled automatically a few days later, and/or
     * have the card accepted without verification. Off unless `enabled` is
     * true. See {@link SaasTestCardAutoRefundConfig}.
     */
    test_card_auto_refund?: SaasTestCardAutoRefundConfig;
    /**
     * The "come back for N% off" coupon minted for a creator who cancelled,
     * shortly before their access ends. See {@link SaasWinBackOfferConfig}.
     */
    win_back_offer?: SaasWinBackOfferConfig;
  };
  /**
   * The master switch for the cancellation flow's save offers — every one of
   * them: the save screen an answer opens (the discount), the free month + AI
   * credits offered at Confirm Cancel, and the older generic retention modal.
   * Off, a creator who cancels goes straight through the survey to the
   * cancellation, with nothing offered. The offers' values live in
   * `saas_subscription.retention_offer`; this only says whether any is shown.
   *
   * ⚠️ ABSENT MEANS ON — read it `!== false`, never `=== true`. Save offers ran
   * before this key existed, so a config that has never written it must keep
   * offering them; only an explicit `false` switches them off.
   */
  offer_retention_on_cancellation?: boolean;
  /**
   * Echo home "Your Journey" projection numbers, JSON-serialized (same
   * storage style as `font_config`). Parsed client-side against the baked
   * defaults (`@ps/features` `journeyConfig.ts`, which also documents the
   * shape). Every figure the journey promises comes from this block —
   * editing it changes what creators are promised. Absent/unparsable → the
   * client falls back to its baked defaults.
   */
  echo_home_journey?: string;
  /**
   * What a signup assumes when the arrival carries NO campaign params — neither
   * `utm_onboarding_path` nor `utm_regintent`.
   *
   * Most arrivals have neither: an organic visit, a link someone pasted with the
   * query stripped, a Meta OAuth round trip that returns to a bare `/signup`.
   * Until this existed those creators were hardcoded to `aiecho` with no
   * onboarding path at all, so they landed on the dashboard having met none of
   * the product — and changing that verdict meant a deploy.
   *
   * ABSENT means "keep the built-in behaviour", so an unset config changes
   * nothing. Only an explicitly written block moves the default.
   *
   * A creator's OWN answer still wins: the real-estate confirmation on the
   * signup card, and any campaign params that ARE present, are both decided
   * before this is consulted. This is the floor, not an override.
   */
  signup_fallback?: {
    /**
     * Written to `profiles/{uid}.businessType`. `null` for "none" — the same
     * spelling, and the same reason, as `onboarding_path` below: an operator
     * turning the override OFF is a decision that has to survive the patch
     * write, and an omitted key would silently keep whatever was saved before.
     *
     * The two are independent. Either may be null while the other carries a
     * value, and both null is the honest way to say "no override at all" — the
     * consumers then behave exactly as they did before this config existed.
     */
    business_type: PopStoreCreatorBusinessType | null;
    /**
     * A `utm_onboarding_path` value to assume, exactly as a campaign link would
     * have spelled it, so the same resolver handles both. `null` for "none" —
     * which is today's behaviour, and is why it is nullable rather than absent:
     * an admin turning the path OFF is a deliberate choice that must outlast a
     * merge, not a missing key.
     */
    onboarding_path: string | null;
  };
}
